import { bassNotes, bassOf, keyOfChord, withBass } from "@bandstand/core/chord";
import styled from "styled-components";

import { color } from "../../theme/color";
import { SmallHeading } from "../ui/controls";

/**
 * The bass notes to play under the picked chord, like the E in C/E. The notes of the key come first; the others are
 * quieter. The chord's own root isn't offered, as C/C is just C. None, or tapping the chosen note again, takes the
 * bass note off.
 */
export function BassNotes(props: { chord: string; songKey: string | undefined; onChange(bass: string | undefined): void }) {
    const { chord, songKey, onChange } = props;
    const current = bassOf(chord);
    const plain = withBass(chord, undefined);
    const notes = bassNotes(songKey ?? keyOfChord(chord) ?? "C").filter(({ note }) => withBass(chord, note) !== plain);

    return (
        <Section role="group" aria-label={`Bass note under ${chord}`}>
            <SmallHeading as="div">Bass note</SmallHeading>
            <Notes>
                <Note aria-pressed={!current} $inKey onClick={() => onChange(undefined)}>
                    None
                </Note>
                {notes.map(({ note, inKey }) => (
                    <Note
                        key={note}
                        aria-pressed={note === current}
                        $inKey={inKey}
                        onClick={() => onChange(note === current ? undefined : note)}
                    >
                        /{note}
                    </Note>
                ))}
            </Notes>
        </Section>
    );
}

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
`;

/** In a row that scrolls sideways on a phone, and in a grid in the side panel on a tablet. */
const Notes = styled.div`
    display: flex;
    gap: 6px;
    overflow-x: auto;

    @media (min-width: 700px) {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        overflow: visible;
    }
`;

const Note = styled.button<{ $inKey: boolean }>`
    flex-shrink: 0;
    min-width: 44px;
    height: 40px;
    padding: 0 6px;
    border-radius: 10px;
    border: 1px solid ${({ $inKey }) => ($inKey ? color.borderStrong : color.border)};
    background: ${color.background};
    color: ${({ $inKey }) => ($inKey ? color.text : color.textMuted)};
    font-size: 14px;
    font-weight: ${({ $inKey }) => ($inKey ? 700 : 500)};
    cursor: pointer;

    &:first-child {
        grid-column: 1 / -1;
    }

    &[aria-pressed="true"] {
        border-color: ${color.accent};
        background: ${color.accent};
        color: ${color.onAccent};
    }
`;
